// src/features/tuition/components/students/StudentForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Divider,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  FormHelperText,
  IconButton
} from '@mui/material';
import { 
  Close as CloseIcon 
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const GRADES = ['R', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const SUBJECTS = [
  'Mathematics', 'Physical Sciences', 'Life Sciences', 'English', 
  'Afrikaans', 'History', 'Geography', 'Accounting', 'Economics',
  'Business Studies', 'Computer Science', 'Information Technology'
];

const StudentForm = ({ open, onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    grade: '',
    school: '',
    email: '',
    contactNumber: '',
    parentName: '',
    parentEmail: '',
    parentContact: '',
    subjects: [],
    goals: '',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        // Ensure all expected fields exist
        goals: initialData.goals || '',
        notes: initialData.notes || ''
      });
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      grade: '',
      school: '',
      email: '',
      contactNumber: '',
      parentName: '',
      parentEmail: '',
      parentContact: '',
      subjects: [],
      goals: '',
      notes: ''
    });
    setErrors({});
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleSubjectsChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      subjects: newValue
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.grade) {
      newErrors.grade = 'Grade is required';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (formData.parentEmail && !/\S+@\S+\.\S+/.test(formData.parentEmail)) {
      newErrors.parentEmail = 'Please enter a valid email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      await onSubmit(formData);
      
      setNotification({
        open: true,
        message: initialData 
          ? 'Student updated successfully!' 
          : 'Student added successfully!',
        severity: 'success'
      });
      
      // Close the form after a short delay to show success message
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error("Error saving student:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
      setLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={loading ? null : onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {initialData ? 'Edit Student' : 'Add New Student'}
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
              Student Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.grade}>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    name="grade"
                    value={formData.grade}
                    label="Grade"
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {GRADES.map(grade => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.grade && (
                    <FormHelperText>{errors.grade}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School"
                  name="school"
                  value={formData.school}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="subjects"
                  options={SUBJECTS}
                  value={formData.subjects}
                  onChange={handleSubjectsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subjects"
                      placeholder="Select subjects"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                      />
                    ))
                  }
                  disabled={loading}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
              Parent/Guardian Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent/Guardian Name"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent/Guardian Contact"
                  name="parentContact"
                  value={formData.parentContact}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Parent/Guardian Email"
                  name="parentEmail"
                  type="email"
                  value={formData.parentEmail}
                  onChange={handleInputChange}
                  error={!!errors.parentEmail}
                  helperText={errors.parentEmail}
                  disabled={loading}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
              Additional Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Learning Goals"
                  name="goals"
                  multiline
                  rows={2}
                  value={formData.goals}
                  onChange={handleInputChange}
                  placeholder="What does the student want to achieve?"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information or special requirements"
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            {loading ? 'Saving...' : initialData ? 'Update Student' : 'Add Student'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StudentForm;